import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEquipmentScope } from '../equipmentScope'
import { AlertTriangle, ExternalLink, Square } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { eventMeta, type MaintenanceTask } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import type { EndEventPayload } from '@/api/equipmentApi'
import { useAuth } from '@/providers/AuthProvider'
import { cn } from '@/lib/utils'
import { mintSigningToken } from '@/lib/signingToken'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SignatureConfirmPanel } from '@/components/renderer/process/SignatureConfirmPanel'
import { usePendingActions } from '@/hooks/usePendingActions'
import { equipmentApi, type PendingActionRow } from '@/api/equipmentApi'
import { procedureRunPath } from '@/lib/procedureRunPath'
import { useTenant } from '@/providers/TenantProvider'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{children}</p>
}

/**
 * End a started maintenance event: capture actual hours, result and any
 * deviation, then apply the 21 CFR Part 11 e-signature. Mirrors StartEventModal.
 */
export function EndEventModal({
  task,
  onClose,
  onConfirm,
}: {
  task: MaintenanceTask | null
  onClose: () => void
  /** Rejects when the server refuses the End; the modal then stays open and says why. */
  onConfirm: (id: string, payload: EndEventPayload) => Promise<void>
}) {
  const { copy } = useEquipmentScope()
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const evt = task ? eventMeta(task.eventType, catalog) : null

  const { email, user } = useAuth()
  const signer = email ?? '' // credential identity — used for verify + audit
  const signerName = user?.fullName || email || '' // display name (never the id/username)

  const [actualHours, setActualHours] = useState('')
  const [result, setResult] = useState('')
  const [deviation, setDeviation] = useState('')
  // The typed name that IS the signature. See lib/signature.ts.
  // The password that IS the signature. See lib/signingToken.ts.
  const [password, setPassword] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  // The server's refusal of the End itself (e.g. the task's procedure is not
  // complete): shown at the top of this modal, where it was asked.
  const [endError, setEndError] = useState<string | null>(null)
  const [opening, setOpening] = useState(false)
  const navigate = useNavigate()
  const { selectedTenantId } = useTenant()

  // A task whose cycle carries a procedure ends only once that run's forms are
  // done (z8nrz7ewt1). Say so before the user signs, not after.
  const pending = usePendingActions(task !== null)
  const gate: PendingActionRow | undefined = task
    ? pending.data?.find((r) => r.eventId === task.id && (r.procedureId === null || r.completedForms < r.totalForms))
    : undefined

  const openProcedure = async () => {
    if (!gate || opening) return
    setOpening(true)
    try {
      const procedureId = gate.procedureId ?? (await equipmentApi.openTaskProcedure(selectedTenantId as string, gate.eventId)).procedureId
      const path = procedureRunPath(gate.purpose === 'ROOM' ? 'Cleaning' : 'Equipment', procedureId, null)
      onClose()
      if (path) navigate(path)
    } catch (err) {
      setEndError(apiErrorMessage(err, "Could not open this task's procedure."))
    } finally {
      setOpening(false)
    }
  }

  useEffect(() => {
    if (!task) return
    setActualHours(task.planHours ? String(task.planHours) : '')
    setResult('')
    setDeviation('')
    setPassword('')
    setSignError(null)
    setEndError(null)
    setVerifying(false)
  }, [task])

  const hoursNum = actualHours.trim() === '' ? undefined : Number(actualHours)
  const hoursValid = hoursNum === undefined || (!Number.isNaN(hoursNum) && hoursNum >= 0)
  // Whether the password is CORRECT is the server's answer, not a client-side
  // check — the button only gates on the form being valid and something typed.
  // Wait for the procedure check before allowing a signature, so one is not
  // spent on an End the server will refuse. Only while it loads: if the check
  // itself fails, End stays possible and the server still enforces the rule.
  const checking = task !== null && pending.isLoading
  const canEnd = hoursValid && signer !== '' && password !== '' && !gate && !checking

  // Minting verifies the password server-side; the token is then spent as the
  // transition is applied, so a wrong password never reaches it.
  const handleConfirm = async () => {
    if (!task || !canEnd || verifying) return
    setSignError(null)
    setEndError(null)
    setVerifying(true)
    let signingToken: string
    try {
      signingToken = await mintSigningToken(password)
    } catch (err) {
      setSignError(apiErrorMessage(err, 'Password could not be verified.'))
      setVerifying(false)
      return
    }
    try {
      await onConfirm(task.id, {
        signingToken,
        signatureMeaning: 'Completed by',
        actualHours: hoursNum,
        result: result.trim() || undefined,
        deviation: deviation.trim() || undefined,
      })
    } catch (err) {
      // The signature was spent on the refused attempt: sign again after fixing it.
      setPassword('')
      setEndError(apiErrorMessage(err, 'The task could not be ended.'))
      void pending.refetch()
    } finally {
      setVerifying(false)
    }
  }

  return (
    <Dialog open={task !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[92vh] gap-0 overflow-y-auto p-0 sm:max-w-lg'>
        {task && evt && (
          <>
            <div className='flex items-start gap-3 border-b border-gray-100 bg-gray-50/60 px-6 py-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600'>
                <Square className='h-5 w-5 fill-current' />
              </div>
              <div>
                <h2 className='text-lg font-semibold tracking-tight text-gray-900'>End Event</h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {task.equipmentId} · {evt.label.replace(/ \(.*\)$/, '')} · record actuals and sign
                </p>
              </div>
            </div>

            <div className='space-y-4 px-6 py-5'>
              {(gate || endError) && (
                <div role='alert' className='flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3'>
                  <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-red-600' />
                  <div className='min-w-0 flex-1 space-y-2'>
                    <p className='text-sm font-semibold text-red-800'>
                      {gate ? 'Complete the procedure before ending this task' : 'This task could not be ended'}
                    </p>
                    <p className='text-xs leading-relaxed text-red-700'>
                      {gate
                        ? `This task's cycle carries the "${gate.procedureTemplateName}" procedure: ${gate.completedForms} of ${gate.totalForms} ${gate.totalForms === 1 ? 'form' : 'forms'} done. Finish it, then come back to end the task.`
                        : endError}
                    </p>
                    {gate && (
                      <button
                        type='button'
                        onClick={() => void openProcedure()}
                        disabled={opening}
                        className='inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60'
                      >
                        <ExternalLink className='h-3.5 w-3.5' />
                        {opening ? 'Opening…' : 'Open procedure'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              <SectionLabel>Actual vs plan</SectionLabel>
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-1'>
                  <p className='text-xs font-medium text-gray-500'>Actual Hours</p>
                  <Input
                    type='number'
                    min={0}
                    step={0.25}
                    value={actualHours}
                    onChange={(e) => setActualHours(e.target.value)}
                    placeholder={`planned ${task.planHours.toFixed(1)}`}
                    className='h-9'
                  />
                </div>
                <div className='space-y-1'>
                  <p className='text-xs font-medium text-gray-500'>Planned Hours</p>
                  <div className='flex h-9 items-center rounded-md border border-gray-200 bg-gray-50/70 px-3 text-sm text-gray-700'>
                    {task.planHours.toFixed(1)}
                  </div>
                </div>
              </div>

              <div className='space-y-1'>
                <p className='text-xs font-medium text-gray-500'>Result</p>
                <textarea
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                  rows={2}
                  placeholder={copy.endPlaceholder}
                  className='w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30'
                />
              </div>

              <div className='space-y-1'>
                <p className='text-xs font-medium text-gray-500'>Deviation / non-conformance (optional)</p>
                <textarea
                  value={deviation}
                  onChange={(e) => setDeviation(e.target.value)}
                  rows={2}
                  placeholder='Record any deviation…'
                  className='w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-red-300 focus:ring-1 focus:ring-red-200'
                />
              </div>
            </div>

            {/* E-signature — the shared panel, so this reads identically to a
                process-step signature. */}
            <div className='border-t border-gray-100 bg-gray-50/60 px-6 py-4'>
              <SignatureConfirmPanel
                meaning='Completed by'
                signerName={signerName}
                value={password}
                onChange={setPassword}
                onSubmit={() => void handleConfirm()}
              />

              <div className='mt-3 flex flex-wrap items-center gap-3'>
                <button
                  type='button'
                  onClick={onClose}
                  className='rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
                >
                  Cancel
                </button>
                <p className={cn('text-[11px]', signError ? 'font-medium text-red-600' : 'text-gray-400')}>
                  {signError ?? 'Status Started → Completed · written to audit trail'}
                </p>
                <button
                  type='button'
                  disabled={!canEnd || verifying}
                  onClick={handleConfirm}
                  className='ml-auto inline-flex items-center gap-2 rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none'
                >
                  <Square className='h-4 w-4 fill-current' />
                  {verifying ? 'Verifying…' : checking ? 'Checking…' : 'End Event'}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
